// Локальное хранилище тайлов графа: IndexedDB (ArrayBuffer без перекодирования),
// до maxEntries записей, вытесняются самые старые. Любая ошибка хранилища
// (приватное окно, квота, нет IndexedDB) - не ошибка маршрутизации: get -> null,
// put -> no-op.
export interface StoredTile {
  key: string;
  etag: string;
  buf: ArrayBuffer;
  savedAt: number;
}

export interface TileStore {
  get(key: string): Promise<StoredTile | null>;
  put(tile: StoredTile): Promise<void>;
}

export function createMemoryTileStore(): TileStore & { size(): number } {
  const m = new Map<string, StoredTile>();
  return {
    get: async (key) => m.get(key) ?? null,
    put: async (tile) => {
      m.set(tile.key, tile);
    },
    size: () => m.size,
  };
}

const DB = "ecoway-routing";
const STORE = "tiles";

const req = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });

export function createIdbTileStore(maxEntries = 120): TileStore {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
        const r = indexedDB.open(DB, 1);
        r.onupgradeneeded = () => {
          const s = r.result.createObjectStore(STORE, { keyPath: "key" });
          s.createIndex("savedAt", "savedAt");
        };
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
      });
    }
    return dbPromise;
  };
  return {
    async get(key) {
      try {
        const db = await open();
        return ((await req(db.transaction(STORE).objectStore(STORE).get(key))) as StoredTile | undefined) ?? null;
      } catch {
        return null;
      }
    },
    async put(tile) {
      try {
        const db = await open();
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        store.put(tile);
        const count = await req(store.count());
        if (count > maxEntries) {
          // самые старые - по индексу savedAt
          let toDrop = count - maxEntries;
          const cursorReq = store.index("savedAt").openCursor();
          await new Promise<void>((resolve) => {
            cursorReq.onsuccess = () => {
              const c = cursorReq.result;
              if (!c || toDrop <= 0) return resolve();
              c.delete();
              toDrop--;
              c.continue();
            };
            cursorReq.onerror = () => resolve();
          });
        }
        await new Promise<void>((resolve) => {
          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
          tx.onabort = () => resolve();
        });
      } catch {
        // хранилище недоступно - работаем без него
      }
    },
  };
}
